import { useState, useRef, useEffect } from 'react';
import { Edit2, Trash2, Copy, MoreVertical, FileText, Calendar, FolderInput, User } from 'lucide-react';
import type { FormListItem } from '@nanoapp/types';
import type { CustomerDTO } from '../../lib/api';
import { CustomerChip } from '../AppCard';

interface Props {
  form: FormListItem;
  /** 2026-05-19: customer pra chip visual + identificar cliente atual. */
  customer?: CustomerDTO | null;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveToFolder?: () => void;
  /** 2026-05-19: abre picker pra atribuir form a um cliente (sem entrar no builder). */
  onAssignCustomer?: () => void;
}

export default function FormCard({ form, customer, onEdit, onDelete, onDuplicate, onMoveToFolder, onAssignCustomer }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const updatedAt = new Date(form.updatedAt).toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  function openMenu() {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setMenuOpen(true);
  }

  // Fecha o menu ao rolar a página
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [menuOpen]);

  function handleDelete() {
    if (confirmDelete) {
      onDelete();
      setMenuOpen(false);
    } else {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
    }
  }

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden group"
      style={customer ? { borderLeft: `4px solid ${customer.color ?? '#6366f1'}` } : undefined}
    >
      {/* Color bar */}
      <div className="h-1.5 bg-gradient-to-r from-indigo-500 to-purple-500" />

      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
              <FileText size={16} className="text-indigo-600" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm leading-tight truncate">
              {form.title || 'Sem título'}
            </h3>
          </div>

          <div className="shrink-0">
            <button
              ref={btnRef}
              onClick={openMenu}
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              <MoreVertical size={16} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-500 mt-4 flex-wrap">
          <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            {form.fieldCount} campo{form.fieldCount !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-1">
            <Calendar size={11} />
            {updatedAt}
          </div>
          {customer ? (
            <CustomerChip customer={customer} compact />
          ) : onAssignCustomer ? (
            <button
              onClick={(e) => { e.stopPropagation(); onAssignCustomer(); }}
              className="text-[10px] text-gray-400 italic hover:text-indigo-600 underline decoration-dotted"
              title="Atribuir a um cliente"
            >
              sem cliente
            </button>
          ) : null}
        </div>

        <button
          onClick={onEdit}
          className="mt-4 w-full py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-medium transition-colors"
        >
          Editar Formulário
        </button>
      </div>

      {/* Dropdown — fixed para escapar do overflow:hidden do card */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-xl min-w-[180px] py-1.5"
            style={{ top: menuPos.top, right: menuPos.right }}
          >
            <button
              onClick={() => { onEdit(); setMenuOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Edit2 size={14} className="text-gray-400" /> Editar
            </button>
            <button
              onClick={() => { onDuplicate(); setMenuOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Copy size={14} className="text-gray-400" /> Duplicar
            </button>
            {onMoveToFolder && (
              <button
                onClick={() => { onMoveToFolder(); setMenuOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FolderInput size={14} className="text-gray-400" /> Mover para pasta
              </button>
            )}
            {/* 2026-05-19: atalho pra atribuir customer sem entrar no builder */}
            {onAssignCustomer && (
              <button
                onClick={() => { onAssignCustomer(); setMenuOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <User size={14} className="text-gray-400" />
                {customer ? 'Trocar cliente' : 'Atribuir a cliente'}
              </button>
            )}
            <div className="my-1 border-t border-gray-100" />
            <button
              onClick={handleDelete}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors rounded-b-xl ${
                confirmDelete
                  ? 'text-red-700 bg-red-50 font-medium'
                  : 'text-red-500 hover:bg-red-50'
              }`}
            >
              <Trash2 size={14} />
              {confirmDelete ? 'Confirmar exclusão' : 'Excluir'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
